import { useState, useMemo, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { runIntegrityAudit, type IntegrityFinding, type IntegritySeverity, type IntegrityCategory } from "@/data/systemIntegrity";
import { SystemAuditService, type AuditAction } from "@/services/system-audit.service";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SuggestedFixCard } from "@/components/SuggestedFixCard";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ShieldCheck, AlertTriangle, AlertOctagon, Info, RefreshCw, Search,
  ExternalLink, Clock, CheckCircle2, Filter, Wrench, FileText,
} from "lucide-react";

const SEVERITY_STYLES: Record<IntegritySeverity, { icon: React.ElementType; color: string; label: string }> = {
  critical: { icon: AlertOctagon, color: "bg-destructive/10 text-destructive border-destructive/30", label: "Critical" },
  warning: { icon: AlertTriangle, color: "bg-warning/10 text-warning border-warning/30", label: "Warning" },
  info: { icon: Info, color: "bg-info/10 text-info border-info/30", label: "Info" },
};

const CATEGORY_LABELS: Record<IntegrityCategory, string> = {
  relationship: "Missing Relationships",
  status: "Status Inconsistency",
  financial: "Financial Integrity",
  document: "Document Governance",
  readiness: "Readiness Gap",
  orphan: "Orphaned Records",
  blocker: "Workflow Blocker",
};

export default function SystemAudit() {
  const {
    jobs, quotes, issues, credits, contractors,
    buildReviewEvents, serviceTickets,
    purchaseOrders, stagingRecords, loadOutChecklists,
    appointmentResults, opportunities, jobsLoading, issuesLoading, creditsLoading, contractorsLoading
  } = useAppData();
  const [search, setSearch] = useState("");
  const [severityFilter, setSeverityFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [lastRunAt, setLastRunAt] = useState<string>("");
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [applied, setApplied] = useState<Set<string>>(new Set());
  // Bug 1 fix: runKey forces the memo to re-evaluate when Re-run is clicked
  const [runKey, setRunKey] = useState(0);
  const [actionLog, setActionLog] = useState<AuditAction[]>([]);

  const isLoading = jobsLoading || issuesLoading || creditsLoading || contractorsLoading;

  // Entity type labels for creating descriptive finding labels
  const entityTypeLabels = useMemo(() => ({
    job: "Job",
    quote: "Opportunity",
    issue: "Issue",
    contractor: "Contractor",
    credit: "Credit",
    system: "System"
  }), []);

  // Restore persisted dismissed/applied state from DB on mount
  useEffect(() => {
    SystemAuditService.getDismissedIds().then(ids => {
      if (ids.size > 0) setDismissed(ids);
    });
    SystemAuditService.getAppliedIds().then(ids => {
      if (ids.size > 0) setApplied(ids);
    });
    SystemAuditService.getActionLog().then(log => setActionLog(log));
  }, []);

  const report = useMemo(() => {
    const r = runIntegrityAudit(
      jobs, quotes, issues, credits, contractors, opportunities,
      buildReviewEvents, serviceTickets,
      purchaseOrders, stagingRecords, loadOutChecklists,
      appointmentResults,
    );
    setLastRunAt(r.runAt);
    // Persist run summary to DB (fire-and-forget)
    SystemAuditService.saveRun(r).catch(() => {});
    return r;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    jobs, quotes, issues, credits, contractors, opportunities,
    buildReviewEvents, serviceTickets,
    purchaseOrders, stagingRecords, loadOutChecklists,
    appointmentResults,
    runKey,
  ]);

   // Helper function to get entity display label for a finding
   const getEntityDisplayLabel = useCallback((finding: IntegrityFinding): string => {
     if (!finding.entityId) {
       return finding.title || finding.id;
     }

     const { entityType, entityId } = finding;

     // Look up actual entity for a more descriptive label
     switch (entityType) {
       case "quote": {
         const opp = quotes.find(o => o.id === entityId);
         return opp?.displayReference || `${entityTypeLabels.quote} ${entityId}`;
       }
       case "job": {
         const job = jobs.find(j => j.id === entityId);
         if (job && job.jobNumber != null) {
           return String(job.jobNumber);
         }
         return `${entityTypeLabels.job} ${entityId}`;
       }
       case "issue": {
         const issue = issues.find(i => i.id === entityId);
         if (issue) {
           return `${entityTypeLabels.issue} ${issue.issueType || issue.description || entityId}`;
         }
         return `${entityTypeLabels.issue} ${entityId}`;
       }
       case "contractor": {
         const contractor = contractors.find(c => c.id === entityId);
         if (contractor) {
           return contractor.companyName || contractor.contactName || `${entityTypeLabels.contractor} ${entityId}`;
         }
         return `${entityTypeLabels.contractor} ${entityId}`;
       }
       case "credit": {
         const credit = credits.find(c => c.id === entityId);
         if (credit) {
           return `Credit $${credit.creditAmount} (${entityId})`;
         }
         return `${entityTypeLabels.credit} ${entityId}`;
       }
       case "system":
         return "System";
       default:
         return `${entityTypeLabels[entityType] || entityType} ${entityId}`;
     }
   }, [quotes, jobs, issues, contractors, credits, entityTypeLabels]);

   // Helper function to get descriptive label for a finding ID
   const getFindingLabel = useCallback((findingId: string): string => {
     const finding = report?.findings.find(f => f.id === findingId);
     if (!finding) return findingId; // fallback to ID if not found (e.g., from prior runs)
     return getEntityDisplayLabel(finding);
   }, [report, getEntityDisplayLabel]);

  const filtered = useMemo(() => {
    let f = report.findings;
    // Bug 2 fix: exclude dismissed and applied findings from All Findings tab
    f = f.filter(x => !dismissed.has(x.id) && !applied.has(x.id));
    if (severityFilter !== "all") f = f.filter(x => x.severity === severityFilter);
    if (categoryFilter !== "all") f = f.filter(x => x.category === categoryFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      f = f.filter(x =>
        x.title.toLowerCase().includes(q) ||
        x.detail.toLowerCase().includes(q) ||
        (x.entityId || "").toLowerCase().includes(q)
      );
    }
    return f;
  }, [report, dismissed, applied, search, severityFilter, categoryFilter]);

  const actionable = useMemo(() =>
    // Bug 3 fix: exclude already-applied findings from Suggested Fixes tab
    report.findings.filter(f => f.severity !== "info" && f.recommendation && !applied.has(f.id)),
    [report, applied]
  );

  const categories = useMemo(() => {
    const cats = new Set(report.findings.map(f => f.category));
    return Array.from(cats).sort();
  }, [report]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ShieldCheck className="h-6 w-6 text-primary" />
              System Integrity Audit
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Validates relationships, statuses, and dependencies across all modules
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}>
              <CardContent className="p-4 text-center">
                <Skeleton className="h-9 w-12 mx-auto" />
                <Skeleton className="h-4 w-16 mx-auto mt-2" />
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs defaultValue="findings" className="space-y-4">
          <TabsList>
            <TabsTrigger value="findings">All Findings</TabsTrigger>
            <TabsTrigger value="fixes">Suggested Fixes</TabsTrigger>
          </TabsList>

          <TabsContent value="findings" className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <Skeleton className="h-10 flex-1 min-w-[200px]" />
              <Skeleton className="h-10 w-[150px]" />
              <Skeleton className="h-10 w-[200px]" />
            </div>
            <div className="space-y-2">
              {[1, 2, 3].map(i => (
                <Card key={i}>
                  <div className="flex items-start gap-3 p-4">
                    <Skeleton className="h-8 w-8 rounded-md" />
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-4 w-48" />
                        <Skeleton className="h-5 w-20 rounded-full" />
                      </div>
                      <Skeleton className="h-3 w-64" />
                      <Skeleton className="h-3 w-56" />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="fixes" className="space-y-4">
            <Skeleton className="h-6 w-64" />
            <div className="space-y-2">
              {[1, 2].map(i => (
                <Card key={i}>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <Skeleton className="h-8 w-8 rounded-md" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-56" />
                        <Skeleton className="h-3 w-full" />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Skeleton className="h-8 w-20" />
                      <Skeleton className="h-8 w-20" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  const handleDismiss = (id: string) => {
    setDismissed(prev => new Set(prev).add(id));
    const finding = report.findings.find(f => f.id === id);
    if (finding) SystemAuditService.recordAction(finding, "dismissed").catch(() => {});
  };

  const handleApply = (id: string) => {
    setApplied(prev => new Set(prev).add(id));
    const finding = report.findings.find(f => f.id === id);
    if (finding) SystemAuditService.recordAction(finding, "applied").catch(() => {});
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-primary" />
            System Integrity Audit
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Validates relationships, statuses, and dependencies across all modules
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastRunAt && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Last run: {lastRunAt && !isNaN(new Date(lastRunAt).getTime()) ? new Date(lastRunAt).toLocaleString() : "—"}
            </span>
          )}
          <Button size="sm" variant="outline" onClick={() => {
            setRunKey(k => k + 1);
          }}>
            <RefreshCw className="h-4 w-4 mr-1" /> Re-run Audit
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className={report.summary.critical > 0 ? "border-destructive/40" : ""}>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-destructive">{report.summary.critical}</div>
            <div className="text-xs text-muted-foreground font-medium mt-1">Critical</div>
          </CardContent>
        </Card>
        <Card className={report.summary.warning > 0 ? "border-warning/40" : ""}>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-warning">{report.summary.warning}</div>
            <div className="text-xs text-muted-foreground font-medium mt-1">Warnings</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-info">{report.summary.info}</div>
            <div className="text-xs text-muted-foreground font-medium mt-1">Informational</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-success">
              {report.summary.total === 0 ? (
                <span className="flex items-center justify-center gap-1">
                  <CheckCircle2 className="h-7 w-7" /> 0
                </span>
              ) : report.summary.total}
            </div>
            <div className="text-xs text-muted-foreground font-medium mt-1">Total Findings</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs: Findings + Suggested Fixes */}
      <Tabs defaultValue="findings" className="space-y-4">
        <TabsList>
          <TabsTrigger value="findings">
            All Findings ({report.findings.length})
          </TabsTrigger>
          <TabsTrigger value="fixes" className="flex items-center gap-1">
            <Wrench className="h-3.5 w-3.5" />
            Suggested Fixes ({actionable.length})
          </TabsTrigger>
          <TabsTrigger value="actions" className="flex items-center gap-1">
            <FileText className="h-3.5 w-3.5" />
            Actions ({actionLog.length})
          </TabsTrigger>
        </TabsList>

        {/* Findings Tab */}
        <TabsContent value="findings" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search findings..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className="w-[150px]">
                <Filter className="h-4 w-4 mr-1" />
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severities</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="info">Info</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[200px]">
                <Filter className="h-4 w-4 mr-1" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{CATEGORY_LABELS[cat as IntegrityCategory] || cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {filtered.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <CheckCircle2 className="h-12 w-12 text-success mx-auto mb-3" />
                <h3 className="text-lg font-semibold">All Checks Passed</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  No integrity issues found matching your filters.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {filtered.map(finding => {
                const sev = SEVERITY_STYLES[finding.severity];
                const Icon = sev.icon;
                return (
                  <Card key={finding.id} className="overflow-hidden">
                    <div className="flex items-start gap-3 p-4">
                      <div className={`mt-0.5 p-1.5 rounded-md ${sev.color}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">{finding.title}</span>
                          <Badge variant="outline" className="text-[10px]">
                            {CATEGORY_LABELS[finding.category]}
                          </Badge>
                          {finding.entityId && (
                            <Badge variant="secondary" className="text-[10px]">{getEntityDisplayLabel(finding)}</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{finding.detail}</p>
                        <p className="text-xs text-primary/80 mt-1 italic">
                          💡 {finding.recommendation}
                        </p>
                      </div>
                      {finding.actionLink && (
                        <Link to={finding.actionLink}>
                          <Button variant="ghost" size="sm" className="shrink-0">
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Button>
                        </Link>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Suggested Fixes Tab */}
        <TabsContent value="fixes" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Review and apply suggested fixes. All actions are logged in the audit trail.
            </p>
            <Badge variant="outline" className="text-xs">
              {applied.size} applied · {dismissed.size} dismissed
            </Badge>
          </div>
          {actionable.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <CheckCircle2 className="h-12 w-12 text-success mx-auto mb-3" />
                <h3 className="text-lg font-semibold">No Fixes Needed</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  All critical and warning items are clear.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
               {actionable.map(finding => (
                 <SuggestedFixCard
                   key={finding.id}
                   finding={finding}
                   categoryLabel={CATEGORY_LABELS[finding.category]}
                   entityLabel={getEntityDisplayLabel(finding)}
                   onDismiss={handleDismiss}
                   onApply={handleApply}
                 />
               ))}
            </div>
          )}
        </TabsContent>

        {/* Actions Tab */}
        <TabsContent value="actions" className="mt-5">
          {actionLog.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <h3 className="text-lg font-semibold">No Actions Yet</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Dismissed and applied findings will appear here.
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 border-b border-border">
                      <tr>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">Finding</th>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">Action</th>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">Performed By</th>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">Note</th>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {actionLog.map(action => (
                        <tr key={action.id} className="hover:bg-muted/30">
                          <td className="px-4 py-3 font-mono text-xs">{getFindingLabel(action.findingId)}</td>
                          <td className="px-4 py-3">
                            <Badge variant={action.actionType === "applied" ? "default" : "secondary"} className="text-[10px]">
                              {action.actionType}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">{action.performedBy || "—"}</td>
                          <td className="px-4 py-3 text-muted-foreground max-w-[200px] truncate">{action.note || "—"}</td>
                          <td className="px-4 py-3 text-muted-foreground text-xs">
                            {action.createdAt && !isNaN(new Date(action.createdAt).getTime()) ? new Date(action.createdAt).toLocaleString() : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
